"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog11 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

const Blog11 = () => {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Campus Insights Admin Dashboard: Your Command Center for Smarter Campus Decisions"
                  date="August 17 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog11}
                alt="Campus Insights Admin Dashboard Forms feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={11} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          In higher education, data is everywhere—buried in spreadsheets, siloed in systems, scattered across departments. The result? Slower decisions, missed opportunities, and overworked teams.
        </IntroParagraph>
        <IntroParagraph>
          The Campus Insights Admin Dashboard changes that—bringing recruitment, retention, operations, and compliance into one real-time, centralized hub. It turns fragmented information into smarter, faster decisions so your campus stays one step ahead.
        </IntroParagraph>
      </IntroSection>

      <BlogSection title="A Live Snapshot of What Matters Most">
        <ProcessDescription>
            No more juggling disconnected reports. The dashboard delivers instant visibility into the metrics that matter:
        </ProcessDescription>
        <FeatureList>
          <FeatureItem>Engagement Metrics – Track how students, faculty, and staff interact with digital channels. Spot early shifts—like a spike in financial aid queries or drop in online course participation—and act fast.</FeatureItem>
          <FeatureItem>Enrollment Insights – Monitor application volumes, acceptance rates, and yield in real time. See exactly where prospects drop off and adjust outreach accordingly.</FeatureItem>
          <FeatureItem>Prospective Student Tracking – Understand where prospective students come from, what they search for, and how they engage, enabling more targeted, higher-conversion communication.</FeatureItem>
          <FeatureItem>Operational Metrics – Measure campus service efficiency, from query response times to ticket resolution rates, ensuring resources go where they have the most impact.</FeatureItem>
          <FeatureItem>Site Integrity Report – Automatically flag outdated information, broken links, expired deadlines, and branding inconsistencies to protect trust and credibility.</FeatureItem>
        </FeatureList>
      </BlogSection>

      <BlogSection title=" Why It Matters Now">
        <ProcessDescription>
            In today’s fast-moving education landscape, waiting for end-of-term reports means reacting, not leading. With the Campus Insights Admin Dashboard, you can:
          </ProcessDescription>
        <FeatureList>
          <FeatureItem>Act the moment trends appear</FeatureItem>
          <FeatureItem>Direct resources where they’ll have the most impact</FeatureItem>
          <FeatureItem>Measure every initiative against clear KPIs</FeatureItem>
          <FeatureItem>Replace guesswork with reliable, live data</FeatureItem>
        </FeatureList>
      </BlogSection>

      <ContentSection title="Admin Dashboard Benefits for Staff" isMainHeading>
        <ProcessDescription>
            The Campus Insights Admin Dashboard is designed to give faculty, advisors, and 
            administrative staff a single, clear view of the information they need to work efficiently and make better decisions.        
        </ProcessDescription>
        <SubSection>
          <SubHeading>1. Centralized Access to Key Data</SubHeading>
          <BodyText>
            No more hunting across spreadsheets, emails, and multiple systems. 
            The dashboard brings student activity, engagement trends, and program performance into one place.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>2. Real-Time Insights</SubHeading>
          <BodyText>
            Staff see live updates on form submissions, inquiries, 
            support tickets, and student progress—enabling faster responses and proactive support.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>3. Task & Workflow Management</SubHeading>
          <BodyText>
             Easily track pending actions, approvals, and escalations. Staff can prioritize based on urgency and deadlines.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>4. Student Engagement Tracking</SubHeading>
          <BodyText>
            Monitor student participation in events, courses, and programs. Identify disengaged students early for outreach.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>5. Streamlined Communication</SubHeading>
          <BodyText>
             Integrated messaging and alerts mean staff can follow up with students or departments directly from the dashboard.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>6. Reporting & Compliance</SubHeading>
          <BodyText>
            Generate instant reports for leadership or compliance requirements, reducing administrative burden.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>7. Customizable Views</SubHeading>
          <BodyText>
            Each staff member can tailor their dashboard to focus on the metrics and tasks most relevant to their role.
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>8. Time Savings & Reduced Errors</SubHeading>
          <BodyText>
            Automation and centralized data reduce manual entry, duplication, and the chance of mistakes.
          </BodyText>
        </SubSection>
      </ContentSection>

      <ContentSection title="Financial Benefits to College Administrators" isMainHeading>
        <SubSection>
          <SubHeading>1. Reduced Administrative Labor Costs</SubHeading>
          <BodyText>
            <FeatureList>
              <FeatureItem>Engagement Metrics – Track how students, faculty, and staff interact with digital channels. Spot early shifts—like a spike in financial aid queries or drop in online course participation—and act fast.</FeatureItem>
              <FeatureItem>If a staff member saves even 2 hours a week across 20 staff members, that’s 160 staff hours a month — the equivalent of a part-time employee’s salary.</FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>2. Improved Student Retention (Tuition Revenue)</SubHeading>
          <BodyText>
            <FeatureList>
                <FeatureItem>
                  Early alerts for disengaged students allow staff to intervene before they drop out.
                </FeatureItem>
                <FeatureItem>
                  Retaining even 5–10 more students per semester can mean tens of thousands of dollars in saved tuition revenue.
                </FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>3. Lower Compliance & Reporting Costs</SubHeading>
          <BodyText>
            <FeatureList>
                <FeatureItem>
                  Automated reporting reduces the hours (and possible consultant fees) needed to prepare for audits and accreditation reviews.
                </FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>4. Better Resource Allocation</SubHeading>
          <BodyText>
            <FeatureList>
                <FeatureItem>
                  Real-time data on event attendance, service usage, and program outcomes ensures funding is directed where it has the most impact — reducing waste.
                </FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>5. Reduced Technology Redundancy</SubHeading>
          <BodyText>
            <FeatureList>
                <FeatureItem>
                  One dashboard can replace multiple point solutions, saving subscription and integration costs.
                </FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>

        <SubSection>
          <SubHeading>6. Faster Response to Prospective Students (Higher Conversion Rates)</SubHeading>
          <BodyText>
            <FeatureList>
                <FeatureItem>
                  Quicker follow-up to inquiries increases the likelihood of enrollment — meaning more tuition revenue without additional marketing spend.
                </FeatureItem>
            </FeatureList>
          </BodyText>
        </SubSection>
      </ContentSection>

      <ContentSection title="Beyond Headcounts: Why Credit Completion Matters">
        <ContentParagraph>
          From Data to Direction          
        </ContentParagraph>
        <ProcessDescription>
            This isn’t just another analytics tool—it’s the nerve center of campus operations. 
            By uniting cross-departmental data, the dashboard turns insights into immediate, 
            strategic action—so every decision moves your institution forward.
        </ProcessDescription>
        <ContentParagraph>
           Tomorrow can start with answers, not questions.          
        </ContentParagraph>
      </ContentSection>

    </ContentContainer>
  );
};

const BlogSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <SectionContent>
        {children}
      </SectionContent>
    </SectionContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ProcessDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;


const FeatureItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  list-style-type: none;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    color: #64636a;
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    line-height: 160%;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
    /* Adjust ::before for smaller screen if needed */
    &::before {
      line-height: 150%;
    }
  }
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 25px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const SubHeading = styled.h4`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 18px;
  }
  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const BodyText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const ExampleText = styled.p`
  width: 100%;
  color: #64636a;
  text-align: center;
  letter-spacing: -0.08px;
  font: 700 16px/160% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

export default Blog11;
